import React, { useState, useEffect } from 'react';
import { useNav } from '../App';
import { Box, Typography, Avatar, CircularProgress } from '@mui/material';
import axios from 'axios';
import { C, SP, SZ } from '../theme';
import { FeedbackModal, useFeedbackTrigger } from './chat/feedback';

// ── STYLES ────────────────────────────────────────────────────────
const S = {
  root: {
    minHeight: '100vh',
    background: C.bgApp,
    padding: `16px ${SP.screenH}px`,
    display: 'flex',
    flexDirection: 'column',
    gap: `${SP.md}px`,
    paddingBottom: `${SZ.navHeight + SP.lg}px`,
  },
  title: {
    fontSize: '22px',
    fontWeight: 600,
    color: C.textPrimary,
    letterSpacing: '-0.3px',
  },
  emptyBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    gap: `${SP.sm}px`,
    paddingTop: '60px',
  },
  emptyText: {
    fontSize: '13px',
    color: C.textMuted,
    textAlign: 'center',
    lineHeight: 1.5,
  },
  checkInBtn: {
    background: C.yellow,
    border: 'none',
    borderRadius: '12px',
    height: `${SZ.btnPrimary}px`,
    padding: '0 24px',
    fontSize: '13px',
    fontWeight: 600,
    color: C.yellowText,
    cursor: 'pointer',
    marginTop: `${SP.sm}px`,
    fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
  },
  card: {
    background: C.bgCard,
    border: `0.5px solid ${C.border}`,
    borderRadius: '14px',
    padding: `${SP.cardPad}px`,
    display: 'flex',
    alignItems: 'center',
    gap: `${SP.md}px`,
    cursor: 'pointer',
    transition: 'border-color 0.15s',
  },
  cardExpired: {
    background: C.bgCard,
    border: `0.5px solid ${C.border}`,
    borderRadius: '14px',
    padding: `${SP.cardPad}px`,
    display: 'flex',
    alignItems: 'center',
    gap: `${SP.md}px`,
    opacity: 0.4,
    cursor: 'default',
  },
  info: { flex: 1, minWidth: 0 },
  nameRow: { display: 'flex', alignItems: 'center', gap: '6px' },
  alias: { fontSize: '15px', fontWeight: 500, color: C.textPrimary },
  unreadDot: { width: '8px', height: '8px', borderRadius: '50%', background: C.orange, flexShrink: 0 },
  venue: { fontSize: '12px', color: C.textMuted, marginTop: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  timerNormal:  { fontSize: '11px', color: C.textMuted,    marginTop: '3px', letterSpacing: '0.04em' },
  timerUrgent:  { fontSize: '11px', color: C.orange,       marginTop: '3px', fontWeight: 600, letterSpacing: '0.04em' },
  timerExpired: { fontSize: '11px', color: C.textDisabled, marginTop: '3px', letterSpacing: '0.04em' },
  chevron: { color: C.textDisabled, fontSize: '20px', flexShrink: 0, lineHeight: 1 },
};

// ── HELPERS ───────────────────────────────────────────────────────
function formatCountdown(seconds) {
  if (seconds <= 0) return 'Expired';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m left`;
  return `${m}m left`;
}

function getTimerStyle(seconds) {
  if (seconds <= 0) return S.timerExpired;
  if (seconds <= 3600) return S.timerUrgent;
  return S.timerNormal;
}

// ── COMPONENT ─────────────────────────────────────────────────────
function Chats() {
  const navigate = useNav();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  // ── UNIFIED FEEDBACK MOUNT POINT ──────────────────────────────
  // This is the only place FeedbackModal renders in the app.
  // useFeedbackTrigger reads nav state on mount (for post-unmatch/delete flows)
  // and exposes openFeedback for direct triggers (expiry tap).
  const {
    feedbackState,
    openFeedback,
    closeFeedback,
  } = useFeedbackTrigger({ navigate });

  useEffect(() => {
    fetchMatches();
    const interval = setInterval(fetchMatches, 5000);
    return () => clearInterval(interval);
  }, []);

  const fetchMatches = async () => {
    try {
      const token = localStorage.getItem('binko_token');
      const res = await axios.get(
        (process.env.REACT_APP_API_BASE || 'https://api.binko.me/api') + '/chat/matches',
        { headers: { Authorization: 'Bearer ' + token } }
      );
      setMatches(res.data.matches || []);
    } catch (err) {
      console.error('Chats fetch failed:', err);
    }
    setLoading(false);
  };

  return (
    <>
      <Box sx={S.root}>

        <Typography sx={S.title}>Chats</Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
            <CircularProgress />
          </Box>
        ) : matches.length === 0 ? (
          <Box sx={S.emptyBox}>
            <Typography sx={{ fontSize: '32px' }}>💬</Typography>
            <Typography sx={S.emptyText}>
              No matches yet.{'\n'}Check in to start meeting people!
            </Typography>
            <button style={S.checkInBtn} onClick={() => navigate('/home')}>
              Check In Now
            </button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: `${SP.sm}px` }}>
            {matches.map((match) => {
              const expired   = match.seconds_remaining <= 0;
              const hasUnread = !expired && match.unread_count > 0;

              return (
                <Box
                  key={match.id}
                  sx={expired ? S.cardExpired : S.card}
                  onClick={() => {
                    if (expired && match.feedback_pending === 1) {
                      openFeedback({
                        matchId:       match.id,
                        otherUserId:   match.other_user_id,
                        triggerSource: 'expiry',
                        skipMet:       false,
                        initialMetInPerson: null,
                      });
                      return;
                    }
                    if (!expired) navigate('/chat/' + match.id);
                    if (expired) navigate('/chat/' + match.id);
                  }}
                >
                  <Avatar sx={{
                    width: SZ.avatarLg, height: SZ.avatarLg, fontSize: '17px',
                    bgcolor: expired ? C.bgElevated : C.teal,
                    color:   expired ? C.textDisabled : '#FFFFFF',
                  }}>
                    {match.other_alias.charAt(0).toUpperCase()}
                  </Avatar>

                  <Box sx={S.info}>
                    <Box sx={S.nameRow}>
                      <Typography sx={S.alias}>{match.other_alias}</Typography>
                      {hasUnread && <Box sx={S.unreadDot} />}
                      {expired && match.feedback_pending === 1 && (
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#E53935', ml: 0.5 }} />
                      )}
                    </Box>
                    <Typography sx={S.venue}>{match.venue_name}</Typography>
                    <Typography sx={getTimerStyle(match.seconds_remaining)}>
                      {expired && match.feedback_pending === 1
                        ? '💬 Tap to leave feedback'
                        : `⏱ ${formatCountdown(match.seconds_remaining)}`}
                    </Typography>
                  </Box>

                  {!expired && <Typography sx={S.chevron}>›</Typography>}
                </Box>
              );
            })}
          </Box>
        )}

      </Box>

      {/* UNIFIED FEEDBACK MODAL — only instance in the app */}
      {feedbackState && (
        <FeedbackModal
          open={!!feedbackState}
          onClose={() => { closeFeedback(); fetchMatches(); }}
          matchId={feedbackState.matchId}
          otherUserId={feedbackState.otherUserId}
          triggerSource={feedbackState.triggerSource}
          skipMet={feedbackState.skipMet}
          initialMetInPerson={feedbackState.initialMetInPerson}
        />
      )}
    </>
  );
}

export default Chats;
